/**
 * browserDb.js
 *
 * Thin promise wrapper around IndexedDB — the browser's built-in database —
 * used to cache downloaded API data locally so pages can render instantly
 * from the cache and only re-download what changed (see dataCache.js).
 *
 * Object stores:
 *   collections – one row per cached dataset:
 *                 { key, items: [...], lastSync, savedAt }
 *                 Used for entity collections that support per-item delta
 *                 merging (tracking runs, CP survey jobs, ...).
 *   queries     – one row per cached request/response pair:
 *                 { key, value, savedAt }
 *                 Used for query-shaped endpoints (server-side pagination,
 *                 filters, map extents) where delta sync does not apply.
 *
 * Every helper fails open: if IndexedDB is unavailable (private browsing,
 * old browser, storage corruption) all reads resolve to undefined and all
 * writes resolve silently, so the app behaves exactly as it did before
 * caching existed.
 */

const DB_NAME = 'corrguard-cache';
const DB_VERSION = 1;

export const STORE_COLLECTIONS = 'collections';
export const STORE_QUERIES = 'queries';

let dbPromise = null;

const isSupported = () =>
	typeof window !== 'undefined' && !!window.indexedDB;

const openDb = () => {
	if (!isSupported()) {return Promise.resolve(null);}
	if (dbPromise) {return dbPromise;}

	dbPromise = new Promise(resolve => {
		let request;
		try {
			request = window.indexedDB.open(DB_NAME, DB_VERSION);
		} catch (err) {
			resolve(null);
			return;
		}
		request.onupgradeneeded = () => {
			const db = request.result;
			if (!db.objectStoreNames.contains(STORE_COLLECTIONS)) {
				db.createObjectStore(STORE_COLLECTIONS, { keyPath: 'key' });
			}
			if (!db.objectStoreNames.contains(STORE_QUERIES)) {
				db.createObjectStore(STORE_QUERIES, { keyPath: 'key' });
			}
		};
		request.onsuccess = () => {
			const db = request.result;
			// If another tab upgrades the schema, close so it can proceed.
			db.onversionchange = () => db.close();
			resolve(db);
		};
		request.onerror = () => resolve(null);
		request.onblocked = () => resolve(null);
	});

	return dbPromise;
};

const withStore = (storeName, mode, work) =>
	openDb().then(db => {
		if (!db) {return undefined;}
		return new Promise(resolve => {
			let tx;
			try {
				tx = db.transaction(storeName, mode);
			} catch (err) {
				resolve(undefined);
				return;
			}
			const store = tx.objectStore(storeName);
			let result;
			try {
				result = work(store);
			} catch (err) {
				resolve(undefined);
				return;
			}
			tx.oncomplete = () => {
				resolve(result && result.result !== undefined ? result.result : undefined);
			};
			tx.onerror = () => resolve(undefined);
			tx.onabort = () => resolve(undefined);
		});
	});

/** Read one row by key.  Resolves undefined on miss or any failure. */
export const dbGet = (storeName, key) =>
	withStore(storeName, 'readonly', store => store.get(key));

/** Insert or replace one row (must contain the `key` property). */
export const dbPut = (storeName, row) =>
	withStore(storeName, 'readwrite', store => store.put(row));

/** Delete one row by key. */
export const dbDelete = (storeName, key) =>
	withStore(storeName, 'readwrite', store => store.delete(key));

/** All keys in [prefix, prefix + U+FFFF) — i.e. string keys starting with prefix. */
export const dbKeysWithPrefix = (storeName, prefix) =>
	openDb().then(db => {
		if (!db) {return [];}
		return new Promise(resolve => {
			let request;
			try {
				const range = window.IDBKeyRange.bound(prefix, `${prefix}￿`);
				request = db
					.transaction(storeName, 'readonly')
					.objectStore(storeName)
					.getAllKeys(range);
			} catch (err) {
				resolve([]);
				return;
			}
			request.onsuccess = () => resolve(request.result || []);
			request.onerror = () => resolve([]);
		});
	});

/** All rows whose key starts with prefix. */
export const dbRowsWithPrefix = (storeName, prefix) =>
	openDb().then(db => {
		if (!db) {return [];}
		return new Promise(resolve => {
			let request;
			try {
				const range = window.IDBKeyRange.bound(prefix, `${prefix}￿`);
				request = db
					.transaction(storeName, 'readonly')
					.objectStore(storeName)
					.getAll(range);
			} catch (err) {
				resolve([]);
				return;
			}
			request.onsuccess = () => resolve(request.result || []);
			request.onerror = () => resolve([]);
		});
	});

/** Wipe the whole cache database (e.g. on logout). */
export const dbClearAll = () =>
	Promise.all([
		withStore(STORE_COLLECTIONS, 'readwrite', store => store.clear()),
		withStore(STORE_QUERIES, 'readwrite', store => store.clear())
	]);
